/**
 * Page header after the mockup: a thin data strip (tenant, dates, comparison,
 * data freshness, and "View scope & measurement", which opens every card's
 * Scope panel), then a large title with a one-line subtitle and optional
 * actions on the right.
 */

import { useSearchParams } from "react-router-dom";
import { fmtDate } from "../lib/format.js";
import { freshnessLine } from "../lib/scope.js";
import { GhostButton } from "./primitives.jsx";

export function DataStrip({ data, isFetching, today }) {
  const [params, setParams] = useSearchParams();
  const open = params.get("scope") === "1";
  const toggle = () =>
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        if (open) next.delete("scope");
        else next.set("scope", "1");
        return next;
      },
      { replace: true },
    );
  const fresh = data ? freshnessLine(data, today) : "";
  const parts = data
    ? [
        data.tenant?.name,
        `${fmtDate(data.window.from)} – ${fmtDate(data.window.to)}`,
        `deltas vs ${fmtDate(data.window.prevFrom)} – ${fmtDate(data.window.prevTo)}`,
        fresh,
      ].filter(Boolean)
    : [];
  const live = Boolean(data?.dataAsOf);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11.5px] text-ink-3">
      <span className="inline-flex min-w-0 items-center gap-2 rounded-full bg-surface-1 py-1 pr-3 pl-2 shadow-card">
        <span aria-hidden className="relative flex size-2 shrink-0">
          {live && !isFetching && <span className="absolute inset-0 animate-ping rounded-full opacity-40 [animation-iteration-count:2]" style={{ background: "var(--status-good)" }} />}
          <span className="relative size-2 rounded-full" style={{ background: live ? "var(--status-good)" : "var(--border-strong)" }} />
        </span>
        <span className="tabular min-w-0 truncate max-sm:whitespace-normal" aria-live="polite">
          {isFetching && data ? "Refreshing…" : parts.join(" · ") || "Loading…"}
        </span>
      </span>
      <span className="ml-auto">
        <GhostButton icon="scope" pressed={open} onClick={toggle} onPage>
          {open ? "Hide scope & measurement" : "View scope & measurement"}
        </GhostButton>
      </span>
    </div>
  );
}

export function PageHeader({ title, subtitle, children, actions }) {
  return (
    <div className="flex animate-rise items-start gap-4">
      <div className="min-w-0">
        {children}
        <h1 className="text-[24px] leading-tight font-semibold tracking-[-0.025em] text-ink sm:text-[28px]">{title}</h1>
        {subtitle && <p className="mt-1.5 max-w-3xl text-[13.5px] text-ink-3">{subtitle}</p>}
      </div>
      {actions && <div className="ml-auto flex shrink-0 gap-2">{actions}</div>}
    </div>
  );
}
